import React, { useState } from 'react';
import { Button, Popover, Row, Col, Typography, Input, Select, DatePicker, Checkbox } from 'antd';
import { Flex } from 'antd';
import { AdvancedFiltersOptions, AdvancedFilterValue } from '../../../models/advancedFilters';
import './styles.css';

interface IProps {
  advancedFiltersOptions: AdvancedFiltersOptions;
  handleAdvancedFiltersOptChange: (
    key: string,
    value: AdvancedFilterValue | AdvancedFilterValue[] | string | string[] | undefined
  ) => void;
  advancedFilters: any;
  isViewLabelMode?: boolean;
  isKeypointMode?: boolean;
  isSegmentationPredictionMode?: boolean;
}

export default function AdvancedFilters({
  advancedFiltersOptions,
  handleAdvancedFiltersOptChange,
  advancedFilters,
  isKeypointMode = false,
  isSegmentationPredictionMode = false,
  isViewLabelMode = false,
}: IProps) {
  const [showAdvancedFilters, setShowAdvancedFilters] = useState<boolean>(false);
  const handleSetShowAdvancedFilters = () => {
    return setShowAdvancedFilters(!showAdvancedFilters);
  };

  const { Search } = Input;

  const content = (
    <Row gutter={[16, 16]} className="advanced-filters-row">
      <Col span={24}>
        <Flex vertical>
          <Typography.Text strong>Dicom ICID or UUID</Typography.Text>
          <Search
            placeholder="Enter a Dicom ICID or UUID"
            value={advancedFilters.dicomICID}
            onChange={e => handleAdvancedFiltersOptChange('dicomICID', e.target.value)}
          />
        </Flex>
      </Col>
      {!isViewLabelMode && (
        <Col span={24}>
          <Flex vertical>
            <Typography.Text strong>
              {isKeypointMode ? 'Keypoint ICID' : 'Segmentation ICID'}
            </Typography.Text>
            <Search
              placeholder={isKeypointMode ? 'Enter Keypoint ICID' : 'Enter Segmentation ICID'}
              value={
                isKeypointMode ? advancedFilters.keypointICID : advancedFilters.segmentationICID
              }
              onChange={e =>
                handleAdvancedFiltersOptChange(
                  isKeypointMode ? 'keypointICID' : 'segmentationICID',
                  e.target.value
                )
              }
            />
          </Flex>
        </Col>
      )}
      {/* Frame ID filter (format: <dicom_icid>_<frame_index>), supports comma-separated list */}
      {!isViewLabelMode && (
        <>
          <Col span={24}>
            <Flex vertical>
              <Typography.Text strong>Frame ID</Typography.Text>
              <Search
                placeholder="Enter frame id(s), e.g. di-02A5-89A0-5504_102 or comma-separated list"
                value={advancedFilters.frameId as string}
                onChange={e => handleAdvancedFiltersOptChange('frameId', e.target.value)}
              />
            </Flex>
          </Col>
          {/* Review status checkboxes */}
          <Col span={24}>
            <Flex vertical>
              <Typography.Text strong>Review Status</Typography.Text>
              <Checkbox.Group
                options={[
                  { label: 'Accepted', value: 'accepted' },
                  { label: 'Denied', value: 'denied' },
                  { label: 'Unreviewed', value: 'unreviewed' },
                ]}
                value={advancedFilters.reviewStatus || []}
                onChange={vals => handleAdvancedFiltersOptChange('reviewStatus', vals as string[])}
              />
            </Flex>
          </Col>
        </>
      )}
      {/* Annotation Set filter: show for segmentation labels, keypoints and view labels (not for segmentation predictions) */}
      {!isSegmentationPredictionMode && (
        <Col span={24}>
          <Flex vertical>
            <Typography.Text strong>Annotation Set Name</Typography.Text>
            <Search
              placeholder="Enter annotation set name"
              value={advancedFilters.annotationSetName as string}
              onChange={e => handleAdvancedFiltersOptChange('annotationSetName', e.target.value)}
            />
          </Flex>
        </Col>
      )}
      <Col span={24}>
        <Flex vertical>
          <Typography.Text strong>Datasource</Typography.Text>
          <Select
            placeholder="Select datasource"
            showSearch
            fieldNames={{
              label: 'name',
              value: 'uuid',
            }}
            filterOption={(input, option) =>
              (option?.name ?? '').toLowerCase().includes(input.toLowerCase())
            }
            value={
              typeof advancedFilters.datasources === 'object' && advancedFilters.datasources
                ? advancedFilters.datasources.uuid
                : undefined
            }
            onChange={(value, option) => {
              if (!option || value === undefined) {
                handleAdvancedFiltersOptChange('datasources', { uuid: '', name: '' });
                return;
              }

              if (typeof option === 'object') {
                const optionObj = option as {
                  uuid?: string;
                  name?: string;
                  value?: string;
                  label?: string;
                };

                const uuid = optionObj.uuid || optionObj.value || value;
                const name = optionObj.name || optionObj.label || String(value);

                handleAdvancedFiltersOptChange('datasources', { uuid, name });
              } else {
                handleAdvancedFiltersOptChange('datasources', {
                  uuid: String(value),
                  name: String(value),
                });
              }
            }}
            options={advancedFiltersOptions?.datasources || []}
          />
        </Flex>
      </Col>
      <Col span={24}>
        <Flex vertical>
          <Typography.Text strong>Study ICID or UUID</Typography.Text>
          <Search
            placeholder="Enter a Study ICID or UUID"
            value={advancedFilters.study}
            onChange={e => handleAdvancedFiltersOptChange('study', e.target.value)}
          />
        </Flex>
      </Col>
      <Col span={24}>
        <Flex vertical>
          <Typography.Text strong>
            {isSegmentationPredictionMode ? 'Source Name' : 'Technician'}
          </Typography.Text>
          <Search
            placeholder={
              isSegmentationPredictionMode ? 'Enter Source Name' : 'Enter Technician ID or Email'
            }
            value={
              isSegmentationPredictionMode
                ? advancedFilters.source_name
                : advancedFilters.technician
            }
            onChange={e =>
              handleAdvancedFiltersOptChange(
                isSegmentationPredictionMode ? 'source_name' : 'technician',
                e.target.value
              )
            }
          />
        </Flex>
      </Col>
      <Col span={24}>
        <Flex vertical>
          <Typography.Text strong>Created At</Typography.Text>
          <DatePicker.RangePicker
            onChange={(dates, dateStrings) => {
              handleAdvancedFiltersOptChange('created_at_after', dateStrings[0] || undefined);
              handleAdvancedFiltersOptChange('created_at_before', dateStrings[1] || undefined);
            }}
          />
        </Flex>
      </Col>
      <Col span={24}>
        <Flex vertical>
          <Typography.Text strong>Updated At</Typography.Text>
          <DatePicker.RangePicker
            onChange={(dates, dateStrings) => {
              handleAdvancedFiltersOptChange('updated_at_after', dateStrings[0] || undefined);
              handleAdvancedFiltersOptChange('updated_at_before', dateStrings[1] || undefined);
            }}
          />
        </Flex>
      </Col>
    </Row>
  );

  return (
    <Flex vertical={false} justify={'flex-end'} style={{ width: '100%' }}>
      <Popover trigger="click" content={content} placement="bottomLeft" arrow={false}>
        <Button
          className="advanced-filters-button"
          type="link"
          onClick={handleSetShowAdvancedFilters}
        >
          Advanced filters
        </Button>
      </Popover>
    </Flex>
  );
}
